import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Save, Plus, Trash2, Package, DollarSign, Building2, Upload, Image as ImageIcon, QrCode, Copy, Star, Info, Check } from 'lucide-react';
import ProductLabelModal from './ProductLabelModal';
import inventoryService from '../services/inventoryService';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const ProductFormComplete = ({ product, onClose, onSuccess, mode = 'create' }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isViewMode = mode === 'view';
  const isEditing = !!product && !isViewMode;
  const fileInputRef = useRef(null);

  // Estados principais
  const [formData, setFormData] = useState({
    nome: '',
    sku: '',
    codigo_interno: '',
    descricao: '',
    categoria_id: '',
    marca_id: '',
    unidade_id: '',
    setor_id: '',
    ativo: true,
    imagem_url: ''
  });

  const [variants, setVariants] = useState([]);
  const [pendingDeletedVariantIds, setPendingDeletedVariantIds] = useState([]);
  const [pendingDeletedSupplierPriceIds, setPendingDeletedSupplierPriceIds] = useState([]);
  const [errors, setErrors] = useState({});
  const [showQuickAdd, setShowQuickAdd] = useState({ category: false, brand: false, unit: false });
  const [quickAddData, setQuickAddData] = useState({ category: '', brand: '', unit: '', unitDesc: '' });
  const [imagemPreview, setImagemPreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [skuAutoGenerated, setSkuAutoGenerated] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [selectedVariantForLabel, setSelectedVariantForLabel] = useState(null);

  // Queries para dados auxiliares
  const { data: categoriesData } = useQuery({
    queryKey: ['inventory-categories', { ativo: true }],
    queryFn: () => inventoryService.getCategories({ ativo: true, limit: 100 })
  });

  const { data: brandsData } = useQuery({
    queryKey: ['inventory-brands'],
    queryFn: inventoryService.getBrands
  });

  const { data: unitsData } = useQuery({
    queryKey: ['inventory-units'],
    queryFn: inventoryService.getUnits
  });

  const { data: suppliersData } = useQuery({
    queryKey: ['suppliers'],
    queryFn: inventoryService.getSuppliers
  });

  const { data: setoresData } = useQuery({
    queryKey: ['inventory-setores'],
    queryFn: () => inventoryService.getSetores({ ativo: true })
  });

  const { data: taxesData } = useQuery({
    queryKey: ['inventory-taxes'],
    queryFn: inventoryService.getTaxes
  });

  // Buscar detalhes completos do produto (com variantes) quando em edição/visualização
  const {
    data: productDetailData,
    isLoading: isLoadingProduct,
  } = useQuery({
    queryKey: ['inventory-product', product?.id],
    queryFn: () => inventoryService.getProduct(product.id),
    enabled: !!product?.id,
  });

  // Inicializar formulário e variantes
  useEffect(() => {
    const source = productDetailData?.data || product;

    if (source) {
      setFormData({
        nome: source.nome || '',
        sku: source.sku || '',
        codigo_interno: source.codigo_interno || '',
        descricao: source.descricao || '',
        categoria_id: source.categoria_id || '',
        marca_id: source.marca_id || '',
        unidade_id: source.unidade_id || '',
        setor_id: source.setor_id || '',
        ativo: source.ativo !== undefined ? source.ativo : true,
        imagem_url: source.imagem_url || '',
      });

      if (source.imagem_url) {
        setImagemPreview(source.imagem_url);
      }

      if (Array.isArray(source.variantes)) {
        setVariants((prevVariants) =>
          source.variantes.map((v) => {
            const existingVar = (prevVariants || []).find((pv) => pv.id === v.id);
            const rawPrices = Array.isArray(v.fornecedores_precos) && v.fornecedores_precos.length > 0
              ? v.fornecedores_precos
              : (existingVar?.fornecedores_precos || []);

            const fornecedores_precos = rawPrices.map((p) => ({
              id: p.id,
              fornecedor_id: String(p.fornecedor_id),
              preco_custo: p.preco_custo != null ? String(p.preco_custo) : '',
              preco_venda: p.preco_venda != null ? String(p.preco_venda) : '',
              referencia_fornecedor: p.referencia_fornecedor || '',
              is_preferencial: p.is_preferencial || false,
            }));

            // Sincronizar preços da variante com o fornecedor preferencial (se houver)
            const pref = fornecedores_precos.find(f => f.is_preferencial) || (fornecedores_precos.length === 1 ? fornecedores_precos[0] : null);
            const syncPrecoCusto = pref && pref.preco_custo !== '' ? pref.preco_custo : (v.preco_custo ?? '');
            const syncPrecoVenda = pref && pref.preco_venda !== '' ? pref.preco_venda : (v.preco_venda ?? '');

            return {
              id: v.id,
              cor: v.cor || '',
              tamanho_label: v.tamanho_label || '',
              medida_text: v.medida_text || '',
              espessura_mm: v.espessura_mm ?? '',
              comprimento_cm: v.comprimento_cm ?? '',
              largura_cm: v.largura_cm ?? '',
              preco_custo: syncPrecoCusto,
              preco_venda: syncPrecoVenda,
              fornecedores_precos,
              sku_variacao: v.sku_variacao || '',
              barcode: v.barcode || '',
              referencia_fornecedor: v.referencia_fornecedor || '',
              acabamento: v.acabamento || '',
              diametro_mm: v.diametro_mm ?? '',
              imposto_id: v.imposto_id ?? '',
              imposto_incluido: v.imposto_incluido || false,
            };
          })
        );
      }
    }
  }, [productDetailData, product]);

  // Carregar preços por fornecedor para cada variante existente ao visualizar/editar (reforço resiliente)
  useEffect(() => {
    const source = productDetailData?.data;
    if (!source || !Array.isArray(source.variantes) || source.variantes.length === 0) return;

    // Se todas as variantes já vieram com fornecedores do backend, não precisa disparar requisições em cascata
    const allHavePrices = source.variantes.every(v => Array.isArray(v.fornecedores_precos) && v.fornecedores_precos.length > 0);
    if (allHavePrices) return;

    const loadSupplierPrices = async () => {
      try {
        const results = await Promise.all(
          source.variantes.map(async (v) => {
            const res = await inventoryService.getVariantSupplierPrices(v.id);
            return {
              varianteId: v.id,
              prices: res.data || [],
            };
          })
        );

        setVariants((prev) =>
          prev.map((variant) => {
            const found = results.find((r) => r.varianteId === variant.id);
            if (!found) return variant;

            const fornecedores_precos = found.prices.map((p) => ({
              id: p.id,
              fornecedor_id: String(p.fornecedor_id),
              preco_custo: p.preco_custo != null ? String(p.preco_custo) : '',
              preco_venda: p.preco_venda != null ? String(p.preco_venda) : '',
              referencia_fornecedor: p.referencia_fornecedor || '',
              is_preferencial: p.is_preferencial || false,
            }));

            // Sincronizar preços da variante com o fornecedor preferencial (se houver)
            const pref = fornecedores_precos.find(f => f.is_preferencial) || (fornecedores_precos.length === 1 ? fornecedores_precos[0] : null);
            const syncPrecoCusto = pref && pref.preco_custo !== '' ? pref.preco_custo : variant.preco_custo;
            const syncPrecoVenda = pref && pref.preco_venda !== '' ? pref.preco_venda : variant.preco_venda;

            return {
              ...variant,
              preco_custo: syncPrecoCusto,
              preco_venda: syncPrecoVenda,
              fornecedores_precos,
            };
          })
        );
      } catch (error) {
        console.error('Erro ao carregar preços por fornecedor da variante:', error);
      }
    };

    loadSupplierPrices();
  }, [productDetailData]);

  // Adicionar variante
  const addVariant = () => {
    setVariants([...variants, {
      id: `temp-${Date.now()}`,
      cor: '',
      tamanho_label: '',
      medida_text: '',
      espessura_mm: '',
      comprimento_cm: '',
      largura_cm: '',
      preco_custo: '',
      preco_venda: '',
      fornecedores_precos: [],
      sku_variacao: '',
      barcode: '',
      referencia_fornecedor: '',
      acabamento: '',
      diametro_mm: '',
      imposto_id: '',
      imposto_incluido: false,
    }]);
  };

  // Mutation para deletar variante
  const deleteVariantMutation = useMutation({
    mutationFn: (variantId) => inventoryService.deleteVariant(variantId),
    onSuccess: () => {
      queryClient.invalidateQueries(['inventory-product', product?.id]);
      toast.success('Variante removida com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao remover variante');
    }
  });

  // Remover variante (deleção adiada para confirmação ao salvar)
  const removeVariant = (variantIndex) => {
    const variant = variants[variantIndex];

    if (variant.id && typeof variant.id === 'number' && variant.id > 0) {
      setPendingDeletedVariantIds(prev => [...prev, variant.id]);
    }

    setVariants(variants.filter((_, i) => i !== variantIndex));
  };

  // Atualizar variante
  const updateVariant = (index, field, value) => {
    const updated = [...variants];
    updated[index] = { ...updated[index], [field]: value };
    setVariants(updated);
  };

  // Duplicar variante
  const duplicateVariant = (variantIndex) => {
    const source = variants[variantIndex];
    const cloned = {
      ...source,
      id: `temp-${Date.now()}`,
      sku_variacao: source.sku_variacao ? `${source.sku_variacao}-COPY` : '',
      barcode: '',
      fornecedores_precos: (source.fornecedores_precos || []).map(p => ({
        id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        fornecedor_id: p.fornecedor_id,
        preco_custo: p.preco_custo,
        preco_venda: p.preco_venda,
        referencia_fornecedor: p.referencia_fornecedor,
        is_preferencial: p.is_preferencial
      }))
    };
    setVariants(prev => [...prev, cloned]);
    toast.success('Variante duplicada com sucesso!');
  };

  // Gerar referência de fornecedor automática baseada no fornecedor + produto + variante (sem conflitos)
  const generateSupplierRefCode = (supplierIdOrName, variant, variantIdx) => {
    if (!supplierIdOrName) return '';

    // Encontrar nome do fornecedor
    let supplierName = '';
    if (typeof supplierIdOrName === 'string' || typeof supplierIdOrName === 'number') {
      const found = suppliers.find(s => String(s.id) === String(supplierIdOrName));
      supplierName = found ? (found.name || found.nome || '') : String(supplierIdOrName);
    } else if (supplierIdOrName?.name || supplierIdOrName?.nome) {
      supplierName = supplierIdOrName.name || supplierIdOrName.nome;
    }

    if (!supplierName) return '';

    // 1. Prefixo do Fornecedor (3 a 4 letras maiúsculas sem acentos)
    const cleanWords = supplierName
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    let supplierPrefix = 'FOR';
    if (cleanWords.length === 1) {
      supplierPrefix = cleanWords[0].substring(0, 4).toUpperCase();
    } else if (cleanWords.length > 1) {
      // Se a primeira palavra tiver pelo menos 3 letras (ex: "Palowa" -> "PAL", "Leo Madeiras" -> "LEO")
      if (cleanWords[0].length >= 3) {
        supplierPrefix = cleanWords[0].substring(0, 3).toUpperCase();
      } else {
        // Sigla das iniciais (ex: "Jr ferragens" -> "JRF")
        supplierPrefix = cleanWords.map(w => w[0]).join('').substring(0, 4).toUpperCase();
      }
    }

    // 2. Base do Produto (SKU do produto ou prefixo do nome)
    let prodPart = formData.sku ? formData.sku.trim() : '';
    if (!prodPart) {
      prodPart = (formData.nome || 'PROD')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]/g, '')
        .substring(0, 4)
        .toUpperCase();
    }

    // 3. Tag da Variante (medida, tamanho, espessura ou índice)
    let varTag = '';
    if (variant?.medida_text && variant.medida_text.toString().trim() !== '') {
      varTag = variant.medida_text.toString().trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    } else if (variant?.tamanho_label && variant.tamanho_label.toString().trim() !== '') {
      varTag = variant.tamanho_label.toString().trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    } else if (variant?.espessura_mm && !isNaN(parseFloat(variant.espessura_mm))) {
      varTag = `${Math.round(parseFloat(variant.espessura_mm))}MM`;
    } else if (variant?.cor && variant.cor.toString().trim() !== '') {
      varTag = variant.cor.toString().trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    } else {
      varTag = `V${(variantIdx ?? 0) + 1}`;
    }

    return `${supplierPrefix}-${prodPart}-${varTag}`.replace(/-+/g, '-');
  };

  // Adicionar preço de fornecedor à variante
  const addSupplierPrice = (variantIndex) => {
    const updated = [...variants];
    const currentList = updated[variantIndex].fornecedores_precos || [];
    const isFirst = currentList.length === 0;

    // Sugerir o primeiro fornecedor da lista global que ainda não foi adicionado nesta variante
    const usedSupplierIds = new Set(currentList.map(p => String(p.fornecedor_id)));
    const nextSupplier = suppliers.find(s => !usedSupplierIds.has(String(s.id)));
    const autoRef = nextSupplier ? generateSupplierRefCode(nextSupplier.id, updated[variantIndex], variantIndex) : '';

    const newPriceItem = {
      id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      fornecedor_id: nextSupplier ? String(nextSupplier.id) : '',
      preco_custo: isFirst && updated[variantIndex].preco_custo ? String(updated[variantIndex].preco_custo) : '',
      preco_venda: isFirst && updated[variantIndex].preco_venda ? String(updated[variantIndex].preco_venda) : '',
      referencia_fornecedor: autoRef || (isFirst && updated[variantIndex].referencia_fornecedor ? updated[variantIndex].referencia_fornecedor : ''),
      is_preferencial: isFirst
    };

    updated[variantIndex].fornecedores_precos = [...currentList, newPriceItem];

    // Se for o primeiro fornecedor adicionado, sincronizar com o topo da variante
    if (isFirst && newPriceItem.preco_custo) {
      updated[variantIndex].preco_custo = newPriceItem.preco_custo;
      if (newPriceItem.preco_venda) {
        updated[variantIndex].preco_venda = newPriceItem.preco_venda;
      }
    }

    setVariants(updated);
  };

  // Remover preço de fornecedor
  const removeSupplierPrice = (variantIndex, priceIndex) => {
    const updated = [...variants];
    const currentList = updated[variantIndex].fornecedores_precos || [];
    const removedItem = currentList[priceIndex];

    // Se o item já existia no banco (ID numérico), registrar para deleção no backend ao salvar
    if (removedItem && typeof removedItem.id === 'number' && removedItem.id > 0) {
      setPendingDeletedSupplierPriceIds(prev => [...prev, removedItem.id]);
    }

    const filteredList = currentList.filter((_, i) => i !== priceIndex);

    // Se o fornecedor removido era preferencial e ainda sobrou algum, promover o primeiro a preferencial
    if (removedItem?.is_preferencial && filteredList.length > 0) {
      filteredList[0].is_preferencial = true;
      if (filteredList[0].preco_custo !== '') updated[variantIndex].preco_custo = filteredList[0].preco_custo;
      if (filteredList[0].preco_venda !== '') updated[variantIndex].preco_venda = filteredList[0].preco_venda;
    }

    updated[variantIndex].fornecedores_precos = filteredList;
    setVariants(updated);
  };

  // Atualizar preço de fornecedor
  const updateSupplierPrice = (variantIndex, priceIndex, field, value) => {
    const updated = [...variants];
    const currentList = [...(updated[variantIndex].fornecedores_precos || [])];

    if (field === 'is_preferencial') {
      // Se marcar como preferencial, desmarcar todos os outros desta variante
      currentList.forEach((item, idx) => {
        item.is_preferencial = idx === priceIndex ? Boolean(value) : false;
      });
      // Sincronizar preços de custo e venda no topo da variante com o preferencial
      if (value) {
        const pref = currentList[priceIndex];
        if (pref.preco_custo !== undefined && pref.preco_custo !== '') {
          updated[variantIndex].preco_custo = pref.preco_custo;
        }
        if (pref.preco_venda !== undefined && pref.preco_venda !== '') {
          updated[variantIndex].preco_venda = pref.preco_venda;
        }
      }
    } else if (field === 'fornecedor_id') {
      // Ao trocar o fornecedor, gerar automaticamente a referência baseada no fornecedor + variante
      const autoRef = generateSupplierRefCode(value, updated[variantIndex], variantIndex);
      currentList[priceIndex] = {
        ...currentList[priceIndex],
        fornecedor_id: value,
        referencia_fornecedor: autoRef || currentList[priceIndex].referencia_fornecedor || ''
      };
    } else {
      currentList[priceIndex] = {
        ...currentList[priceIndex],
        [field]: value
      };

      // Se o fornecedor sendo editado é o preferencial, sincronizar em tempo real com o topo da variante
      if (currentList[priceIndex].is_preferencial) {
        if (field === 'preco_custo') updated[variantIndex].preco_custo = value;
        if (field === 'preco_venda') updated[variantIndex].preco_venda = value;
      }
    }

    updated[variantIndex].fornecedores_precos = currentList;
    setVariants(updated);
  };

  // Replicar fornecedor selecionado para todas as outras variantes do produto
  const applySupplierToAllVariants = (sourceVariantIndex, sourcePriceIndex) => {
    const sourcePrice = variants[sourceVariantIndex]?.fornecedores_precos?.[sourcePriceIndex];
    if (!sourcePrice || !sourcePrice.fornecedor_id) {
      toast.error('Selecione um fornecedor antes de replicar');
      return;
    }

    const supplierObj = suppliers.find(s => String(s.id) === String(sourcePrice.fornecedor_id));
    const supplierName = supplierObj?.name || supplierObj?.nome || 'Fornecedor';

    setVariants(prev => prev.map((v, idx) => {
      if (idx === sourceVariantIndex) return v;

      const existingPrices = v.fornecedores_precos || [];
      const existingIdx = existingPrices.findIndex(p => String(p.fornecedor_id) === String(sourcePrice.fornecedor_id));
      const autoRef = generateSupplierRefCode(sourcePrice.fornecedor_id, v, idx);

      let newPrices;
      if (existingIdx >= 0) {
        newPrices = [...existingPrices];
        newPrices[existingIdx] = {
          ...newPrices[existingIdx],
          preco_custo: sourcePrice.preco_custo,
          preco_venda: sourcePrice.preco_venda,
          referencia_fornecedor: newPrices[existingIdx].referencia_fornecedor || autoRef,
          is_preferencial: sourcePrice.is_preferencial
        };
      } else {
        newPrices = [
          ...existingPrices,
          {
            id: `temp-${Date.now()}-${idx}-${Math.random().toString(36).substr(2, 4)}`,
            fornecedor_id: sourcePrice.fornecedor_id,
            preco_custo: sourcePrice.preco_custo,
            preco_venda: sourcePrice.preco_venda,
            referencia_fornecedor: autoRef,
            is_preferencial: existingPrices.length === 0 ? true : sourcePrice.is_preferencial
          }
        ];
      }

      if (sourcePrice.is_preferencial) {
        newPrices.forEach((p) => {
          p.is_preferencial = String(p.fornecedor_id) === String(sourcePrice.fornecedor_id);
        });
      }

      const pref = newPrices.find(p => p.is_preferencial) || newPrices[0];

      return {
        ...v,
        preco_custo: pref?.preco_custo !== undefined && pref.preco_custo !== '' ? pref.preco_custo : v.preco_custo,
        preco_venda: pref?.preco_venda !== undefined && pref.preco_venda !== '' ? pref.preco_venda : v.preco_venda,
        fornecedores_precos: newPrices
      };
    }));

    toast.success(`Fornecedor "${supplierName}" replicado para todas as variantes com referências automáticas!`);
  };

  // Mutations para criar dados auxiliares
  const createCategoryMutation = useMutation({
    mutationFn: inventoryService.createCategory,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['inventory-categories']);
      toast.success('Categoria criada com sucesso!');
      setShowQuickAdd({ ...showQuickAdd, category: false });
      setQuickAddData({ ...quickAddData, category: '' });
      setFormData({ ...formData, categoria_id: data.data.id });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao criar categoria');
    }
  });

  const createBrandMutation = useMutation({
    mutationFn: inventoryService.createBrand,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['inventory-brands']);
      toast.success('Marca criada com sucesso!');
      setShowQuickAdd({ ...showQuickAdd, brand: false });
      setQuickAddData({ ...quickAddData, brand: '' });
      setFormData({ ...formData, marca_id: data.data.id });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao criar marca');
    }
  });

  const createUnitMutation = useMutation({
    mutationFn: inventoryService.createUnit,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['inventory-units']);
      toast.success('Unidade criada com sucesso!');
      setShowQuickAdd({ ...showQuickAdd, unit: false });
      setQuickAddData({ ...quickAddData, unit: '', unitDesc: '' });
      setFormData({ ...formData, unidade_id: data.data.id });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao criar unidade');
    }
  });

  // Handler para upload de imagem
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo e tamanho
    if (!file.type.match(/^image\/(jpeg|jpg|png|gif|webp)$/)) {
      toast.error('Apenas arquivos de imagem (JPEG, PNG, GIF, WebP) são permitidos');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const result = await inventoryService.uploadProductImage(file);
      if (result.success) {
        setFormData({ ...formData, imagem_url: result.data.url });
        setImagemPreview(result.data.url);
        toast.success('Imagem uploadada com sucesso!');
      } else {
        toast.error(result.message || 'Erro ao fazer upload da imagem');
      }
    } catch (error) {
      console.error('Erro no upload:', error);
      toast.error('Erro ao fazer upload da imagem');
    } finally {
      setUploadingImage(false);
    }
  };

  // Handler para remover imagem
  const handleRemoveImage = () => {
    setFormData({ ...formData, imagem_url: '' });
    setImagemPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handler para categoria (com geração de SKU automático em tempo real)
  const handleCategoryChange = async (categoryId) => {
    if (isViewMode) return;
    setFormData(prev => ({ ...prev, categoria_id: categoryId }));

    if (!isEditing && categoryId) {
      const categoriesList = categoriesData?.data || [];
      const category = categoriesList.find(c => c.id === parseInt(categoryId));
      if (category) {
        try {
          const result = await inventoryService.getNextSku(category.nome);
          if (result?.success && result.data?.sku) {
            setFormData(prev => ({ ...prev, categoria_id: categoryId, sku: result.data.sku }));
            setSkuAutoGenerated(true);
          }
        } catch (err) {
          console.error('Erro ao gerar SKU em tempo real:', err);
        }
      }
    }
  };

  // Mutation para criar produto
  const createProductMutation = useMutation({
    mutationFn: async (data) => {
      let produtoId = null;
      try {
        // Criar produto primeiro
        const productResponse = await inventoryService.createProduct({
          nome: data.nome,
          sku: data.sku,
          codigo_interno: data.codigo_interno,
          descricao: data.descricao,
          categoria_id: data.categoria_id || null,
          marca_id: data.marca_id || null,
          unidade_id: data.unidade_id || null,
          setor_id: data.setor_id || null,
          ativo: data.ativo,
          imagem_url: data.imagem_url || null
        });

        produtoId = productResponse.data.id;
        const baseSku = data.sku || productResponse.data.sku || 'VAR';

        // Criar variantes com SKU único inteligente para evitar colisão UNIQUE
        const usedSkusInBatch = new Set();
        for (let i = 0; i < data.variants.length; i++) {
          const variant = data.variants[i];
          let varSku = variant.sku_variacao && variant.sku_variacao.toString().trim() !== ''
            ? variant.sku_variacao.trim()
            : null;

          if (!varSku) {
            if (variant.medida_text && variant.medida_text.toString().trim() !== '') {
              varSku = `${baseSku}-${variant.medida_text.toString().trim().toUpperCase().replace(/[^A-Z0-9]/g, '')}`;
            } else if (variant.tamanho_label && variant.tamanho_label.toString().trim() !== '') {
              varSku = `${baseSku}-${variant.tamanho_label.toString().trim().toUpperCase().replace(/[^A-Z0-9]/g, '')}`;
            } else if (variant.espessura_mm && !isNaN(parseFloat(variant.espessura_mm))) {
              varSku = `${baseSku}-${Math.round(parseFloat(variant.espessura_mm))}MM`;
            } else if (variant.cor && variant.cor.toString().trim() !== '') {
              varSku = `${baseSku}-${variant.cor.toString().trim().toUpperCase().replace(/[^A-Z0-9]/g, '')}`;
            } else {
              varSku = `${baseSku}-V${i + 1}`;
            }
          }

          // Garantir que não haja colisão entre as variantes do próprio formulário
          let candidate = varSku;
          let counter = 1;
          while (usedSkusInBatch.has(candidate)) {
            counter++;
            candidate = `${varSku}-${counter}`;
          }
          usedSkusInBatch.add(candidate);
          varSku = candidate;

          const variantResponse = await inventoryService.createVariant(produtoId, {
            cor: variant.cor || null,
            tamanho_label: variant.tamanho_label || null,
            medida_text: variant.medida_text || null,
            espessura_mm: variant.espessura_mm || null,
            comprimento_cm: variant.comprimento_cm || null,
            largura_cm: variant.largura_cm || null,
            preco_custo: parseFloat(variant.preco_custo) || 0,
            preco_venda: parseFloat(variant.preco_venda) || 0,
            ativo: true,
            sku_variacao: varSku,
            barcode: variant.barcode || null,
            referencia_fornecedor: variant.referencia_fornecedor || null,
            acabamento: variant.acabamento || null,
            diametro_mm: variant.diametro_mm ? parseFloat(variant.diametro_mm) : null,
            imposto_id: variant.imposto_id || null,
            imposto_incluido: variant.imposto_incluido || false,
          });

          const varianteId = variantResponse.data.id;

          // Criar preços por fornecedor
          for (const supplierPrice of variant.fornecedores_precos || []) {
            const custoVal = supplierPrice.preco_custo !== undefined && supplierPrice.preco_custo !== ''
              ? parseFloat(supplierPrice.preco_custo)
              : (supplierPrice.preco ? parseFloat(supplierPrice.preco) : null);

            if (supplierPrice.fornecedor_id && custoVal !== null && !isNaN(custoVal)) {
              const vendaVal = supplierPrice.preco_venda !== undefined && supplierPrice.preco_venda !== ''
                ? parseFloat(supplierPrice.preco_venda)
                : null;

              const finalRef = (supplierPrice.referencia_fornecedor && supplierPrice.referencia_fornecedor.trim())
                ? supplierPrice.referencia_fornecedor.trim()
                : generateSupplierRefCode(supplierPrice.fornecedor_id, variant, i);

              await inventoryService.upsertSupplierPrice(varianteId, {
                fornecedor_id: parseInt(supplierPrice.fornecedor_id),
                preco_custo: custoVal,
                preco_venda: vendaVal && !isNaN(vendaVal) ? vendaVal : null,
                referencia_fornecedor: finalRef || null,
                is_preferencial: supplierPrice.is_preferencial || false,
                ativo: true
              });
            }
          }
        }

        return productResponse;
      } catch (err) {
        // Se o produto pai foi criado mas alguma variante falhou, excluir o produto órfão para não travar o SKU
        if (produtoId) {
          try {
            await inventoryService.deleteProduct(produtoId);
          } catch (delErr) {
            console.warn('Erro ao limpar produto parcial:', delErr);
          }
        }
        throw err;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['inventory-products']);
      queryClient.invalidateQueries(['inventory-stats']);
      toast.success('Produto criado com sucesso!');
      onSuccess?.(data);
      onClose();
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || 'Erro ao criar produto';
      toast.error(errorMessage);
      if (error.response?.data?.errors) {
        const validationErrors = {};
        error.response.data.errors.forEach(err => {
          validationErrors[err.path] = err.msg;
        });
        setErrors(validationErrors);
      }
    }
  });

  // Mutation para atualizar produto existente (dados básicos + variantes + preços por fornecedor)
  const updateProductMutation = useMutation({
    mutationFn: async (data) => {
      const produtoId = data.id;

      // Atualizar dados básicos do produto
      await inventoryService.updateProduct(produtoId, {
        nome: data.nome,
        sku: data.sku,
        codigo_interno: data.codigo_interno,
        descricao: data.descricao,
        categoria_id: data.categoria_id || null,
        marca_id: data.marca_id || null,
        unidade_id: data.unidade_id || null,
        setor_id: data.setor_id || null,
        ativo: data.ativo,
        imagem_url: data.imagem_url || null,
      });

      // Atualizar variantes existentes e criar novas
      for (const variant of data.variants) {
        // Se a variante já existe no banco (id numérico positivo), atualizar
        if (variant.id && typeof variant.id === 'number' && variant.id > 0) {
          // Atualizar dados da variante (incluindo preços de custo e venda)
          const precoCusto = variant.preco_custo && variant.preco_custo.toString().trim() !== '' 
            ? parseFloat(variant.preco_custo) 
            : 0;
          const precoVenda = variant.preco_venda && variant.preco_venda.toString().trim() !== '' 
            ? parseFloat(variant.preco_venda) 
            : 0;
          
          await inventoryService.updateVariant(variant.id, {
            cor: variant.cor && variant.cor.toString().trim() !== '' ? variant.cor.trim() : null,
            tamanho_label: variant.tamanho_label && variant.tamanho_label.toString().trim() !== '' ? variant.tamanho_label.trim() : null,
            medida_text: variant.medida_text && variant.medida_text.toString().trim() !== '' ? variant.medida_text.trim() : null,
            espessura_mm: variant.espessura_mm && variant.espessura_mm.toString().trim() !== '' ? parseFloat(variant.espessura_mm) : null,
            preco_custo: precoCusto,
            preco_venda: precoVenda,
            ativo: true,
            sku_variacao: variant.sku_variacao && variant.sku_variacao.toString().trim() !== '' ? variant.sku_variacao.trim() : null,
            barcode: variant.barcode && variant.barcode.toString().trim() !== '' ? variant.barcode.trim() : null,
            referencia_fornecedor: variant.referencia_fornecedor && variant.referencia_fornecedor.toString().trim() !== '' ? variant.referencia_fornecedor.trim() : null,
            acabamento: variant.acabamento && variant.acabamento.toString().trim() !== '' ? variant.acabamento.trim() : null,
            diametro_mm: variant.diametro_mm && variant.diametro_mm.toString().trim() !== '' ? parseFloat(variant.diametro_mm) : null,
            imposto_id: variant.imposto_id || null,
            imposto_incluido: variant.imposto_incluido || false,
          });

          // Atualizar preços por fornecedor
          for (const supplierPrice of variant.fornecedores_precos || []) {
            const custoVal = supplierPrice.preco_custo !== undefined && supplierPrice.preco_custo !== ''
              ? parseFloat(supplierPrice.preco_custo)
              : (supplierPrice.preco ? parseFloat(supplierPrice.preco) : null);

            if (supplierPrice.fornecedor_id && custoVal !== null && !isNaN(custoVal)) {
              const vendaVal = supplierPrice.preco_venda !== undefined && supplierPrice.preco_venda !== ''
                ? parseFloat(supplierPrice.preco_venda)
                : null;

              const finalRef = (supplierPrice.referencia_fornecedor && supplierPrice.referencia_fornecedor.trim())
                ? supplierPrice.referencia_fornecedor.trim()
                : generateSupplierRefCode(supplierPrice.fornecedor_id, variant, data.variants.indexOf(variant));

              await inventoryService.upsertSupplierPrice(variant.id, {
                fornecedor_id: parseInt(supplierPrice.fornecedor_id),
                preco_custo: custoVal,
                preco_venda: vendaVal && !isNaN(vendaVal) ? vendaVal : null,
                referencia_fornecedor: finalRef || null,
                is_preferencial: supplierPrice.is_preferencial || false,
                ativo: true,
              });
            }
          }
        } else {
          // Se é uma nova variante (id temporário), criar
          const precoCusto = variant.preco_custo && variant.preco_custo.toString().trim() !== '' 
            ? parseFloat(variant.preco_custo) 
            : 0;
          const precoVenda = variant.preco_venda && variant.preco_venda.toString().trim() !== '' 
            ? parseFloat(variant.preco_venda) 
            : 0;
          
          const variantResponse = await inventoryService.createVariant(produtoId, {
            cor: variant.cor && variant.cor.toString().trim() !== '' ? variant.cor.trim() : null,
            tamanho_label: variant.tamanho_label && variant.tamanho_label.toString().trim() !== '' ? variant.tamanho_label.trim() : null,
            medida_text: variant.medida_text && variant.medida_text.toString().trim() !== '' ? variant.medida_text.trim() : null,
            espessura_mm: variant.espessura_mm && variant.espessura_mm.toString().trim() !== '' ? parseFloat(variant.espessura_mm) : null,
            preco_custo: precoCusto,
            preco_venda: precoVenda,
            ativo: true,
            sku_variacao: variant.sku_variacao && variant.sku_variacao.toString().trim() !== '' ? variant.sku_variacao.trim() : null,
            barcode: variant.barcode && variant.barcode.toString().trim() !== '' ? variant.barcode.trim() : null,
            referencia_fornecedor: variant.referencia_fornecedor && variant.referencia_fornecedor.toString().trim() !== '' ? variant.referencia_fornecedor.trim() : null,
            acabamento: variant.acabamento && variant.acabamento.toString().trim() !== '' ? variant.acabamento.trim() : null,
            diametro_mm: variant.diametro_mm && variant.diametro_mm.toString().trim() !== '' ? parseFloat(variant.diametro_mm) : null,
            imposto_id: variant.imposto_id || null,
            imposto_incluido: variant.imposto_incluido || false,
          });

          const varianteId = variantResponse.data.id;

          // Criar preços por fornecedor para a nova variante
          for (const supplierPrice of variant.fornecedores_precos || []) {
            const custoVal = supplierPrice.preco_custo !== undefined && supplierPrice.preco_custo !== ''
              ? parseFloat(supplierPrice.preco_custo)
              : (supplierPrice.preco ? parseFloat(supplierPrice.preco) : null);

            if (supplierPrice.fornecedor_id && custoVal !== null && !isNaN(custoVal)) {
              const vendaVal = supplierPrice.preco_venda !== undefined && supplierPrice.preco_venda !== ''
                ? parseFloat(supplierPrice.preco_venda)
                : null;

              const finalRef = (supplierPrice.referencia_fornecedor && supplierPrice.referencia_fornecedor.trim())
                ? supplierPrice.referencia_fornecedor.trim()
                : generateSupplierRefCode(supplierPrice.fornecedor_id, variant, data.variants.indexOf(variant));

              await inventoryService.upsertSupplierPrice(varianteId, {
                fornecedor_id: parseInt(supplierPrice.fornecedor_id),
                preco_custo: custoVal,
                preco_venda: vendaVal && !isNaN(vendaVal) ? vendaVal : null,
                referencia_fornecedor: finalRef || null,
                is_preferencial: supplierPrice.is_preferencial || false,
                ativo: true,
              });
            }
          }
        }
      }

      // Deletar preços de fornecedores que foram removidos durante a edição
      for (const priceId of pendingDeletedSupplierPriceIds) {
        try {
          await inventoryService.deleteSupplierPrice(priceId);
        } catch (err) {
          console.warn('Erro ao deletar preco de fornecedor pendente:', priceId, err);
        }
      }

      // Deletar variantes que foram removidas durante a edição (adiada para o submit)
      for (const variantId of pendingDeletedVariantIds) {
        try {
          await inventoryService.deleteVariant(variantId);
        } catch (err) {
          console.warn('Erro ao deletar variante pendente:', variantId, err);
        }
      }
    },
    onSuccess: () => {
      setPendingDeletedVariantIds([]);
      setPendingDeletedSupplierPriceIds([]);
      queryClient.invalidateQueries(['inventory-products']);
      queryClient.invalidateQueries(['inventory-product', product?.id]);
      queryClient.invalidateQueries(['inventory-stats']);
      toast.success('Produto atualizado com sucesso!');
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || 'Erro ao atualizar produto';
      toast.error(errorMessage);
    }
  });

  const categories = useMemo(() => {
    const list = categoriesData?.data || [];
    // Apenas categorias ativas (se o produto atual já tiver uma categoria inativa selecionada, mantê-la visível para não perder o vínculo histórico)
    return list.filter(cat => cat.ativo !== false || String(cat.id) === String(formData.categoria_id));
  }, [categoriesData, formData.categoria_id]);

  // Handler de submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = {};

    if (!formData.nome || formData.nome.trim() === '') {
      validationErrors.nome = 'Nome do produto é obrigatório';
    }

    if (!formData.unidade_id) {
      validationErrors.unidade_id = 'Unidade é obrigatória';
    }

    if (!formData.categoria_id) {
      validationErrors.categoria_id = 'Categoria é obrigatória';
    }

    if (!formData.setor_id) {
      validationErrors.setor_id = 'O setor da fábrica é obrigatório';
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    if (variants.length === 0) {
      toast.error('Adicione pelo menos uma variante ao produto');
      return;
    }

    setErrors({});

    // Gerar SKU automaticamente se não existir (ao clicar em salvar)
    let finalFormData = { ...formData };
    if (!isEditing && !finalFormData.sku && finalFormData.categoria_id) {
      const category = categories.find(c => c.id === parseInt(finalFormData.categoria_id));
      if (category) {
        try {
          const result = await inventoryService.getNextSku(category.nome);
          if (result.success) {
            finalFormData = { ...finalFormData, sku: result.data.sku };
            setSkuAutoGenerated(true);
          }
        } catch (error) {
          console.error('Erro ao gerar SKU:', error);
        }
      }
    }

    // Garantir que cada variante receba seu SKU único gerado automaticamente pelo sistema se deixado em branco
    const baseSku = finalFormData.sku || 'PROD';
    const usedSkusInBatch = new Set();
    const variantsWithAutoSku = variants.map((v, idx) => {
      let vSku = v.sku_variacao && v.sku_variacao.toString().trim() !== '' ? v.sku_variacao.trim() : '';
      if (!vSku) {
        if (v.medida_text && v.medida_text.toString().trim() !== '') {
          vSku = `${baseSku}-${v.medida_text.toString().trim().toUpperCase().replace(/[^A-Z0-9]/g, '')}`;
        } else if (v.tamanho_label && v.tamanho_label.toString().trim() !== '') {
          vSku = `${baseSku}-${v.tamanho_label.toString().trim().toUpperCase().replace(/[^A-Z0-9]/g, '')}`;
        } else if (v.espessura_mm && !isNaN(parseFloat(v.espessura_mm))) {
          vSku = `${baseSku}-${Math.round(parseFloat(v.espessura_mm))}MM`;
        } else if (v.cor && v.cor.toString().trim() !== '') {
          vSku = `${baseSku}-${v.cor.toString().trim().toUpperCase().replace(/[^A-Z0-9]/g, '')}`;
        } else {
          vSku = `${baseSku}-V${idx + 1}`;
        }
      }

      // Garantir desambiguação dentro do próprio lote do formulário
      let candidate = vSku;
      let counter = 1;
      while (usedSkusInBatch.has(candidate)) {
        counter++;
        candidate = `${vSku}-${counter}`;
      }
      usedSkusInBatch.add(candidate);
      return { ...v, sku_variacao: candidate };
    });

    const payload = {
      ...finalFormData,
      variants: variantsWithAutoSku,
      id: product?.id,
    };

    if (isEditing) {
      updateProductMutation.mutate(payload);
    } else {
      createProductMutation.mutate(payload);
    }
  };

  const brands = brandsData?.data || [];
  const units = unitsData?.data || [];
  const setores = setoresData?.data || [];
  const suppliers = suppliersData?.data || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 lg:p-6 border-b">
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6 text-primary-600" />
            <h2 className="text-2xl font-bold text-secondary-900">
              {isViewMode
                ? 'Visualizar Produto'
                : isEditing
                ? 'Editar Produto'
                : 'Novo Produto Completo'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-secondary-400 hover:text-secondary-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        {isEditing && isLoadingProduct ? (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center text-secondary-600">
              Carregando dados do produto...
            </div>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6">
          {/* Dados do Produto */}
          <div className="mb-4 sm:mb-6 lg:mb-8">
            <h3 className="text-base sm:text-lg font-semibold text-secondary-900 mb-3 sm:mb-4">Dados do Produto</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Nome do Produto *
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => {
                    if (errors.nome) setErrors({ ...errors, nome: undefined });
                    !isViewMode && setFormData({ ...formData, nome: e.target.value });
                  }}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none ${errors.nome ? 'border-red-500 focus:ring-2 focus:ring-red-500' : 'border-secondary-300 focus:ring-2 focus:ring-primary-500'} ${isViewMode ? 'bg-secondary-50 text-secondary-700' : ''}`}
                  disabled={isViewMode}
                />
                {errors.nome && <span className="text-red-500 text-xs">{errors.nome}</span>}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  SKU
                  {skuAutoGenerated && <span className="ml-2 text-xs text-green-600 font-normal">(auto)</span>}
                </label>
                <input
                  type="text"
                  value={formData.sku}
                  readOnly
                  className={`w-full px-3 py-2 border border-secondary-300 rounded-md ${isViewMode || !formData.sku ? 'bg-secondary-50 text-secondary-500' : 'bg-green-50 text-green-700 font-medium'}`}
                  placeholder="Selecione uma categoria"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-secondary-700">
                    Categoria
                  </label>
                  {!isViewMode && (
                    <button
                      type="button"
                      onClick={() => setShowQuickAdd({ ...showQuickAdd, category: !showQuickAdd.category })}
                      className="text-xs text-primary-600 hover:text-primary-800"
                    >
                      + Nova
                    </button>
                  )}
                </div>
                {!isViewMode && showQuickAdd.category ? (
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={quickAddData.category}
                      onChange={(e) => setQuickAddData({ ...quickAddData, category: e.target.value })}
                      placeholder="Nome da categoria"
                      className="flex-1 px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => createCategoryMutation.mutate({ nome: quickAddData.category })}
                      disabled={!quickAddData.category || createCategoryMutation.isPending || createCategoryMutation.isLoading}
                      className="px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 text-sm"
                    >
                      {createCategoryMutation.isPending || createCategoryMutation.isLoading ? '...' : 'Salvar'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowQuickAdd({ ...showQuickAdd, category: false })}
                      className="px-3 py-2 bg-secondary-200 text-secondary-700 rounded-md hover:bg-secondary-300 text-sm"
                    >
                      Cancelar
                    </button>
                  </div>
                ) : null}
                <select
                  value={formData.categoria_id}
                  onChange={(e) => {
                    if (errors.categoria_id) setErrors({ ...errors, categoria_id: undefined });
                    handleCategoryChange(e.target.value);
                  }}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none ${errors.categoria_id ? 'border-red-500 focus:ring-2 focus:ring-red-500' : 'border-secondary-300 focus:ring-2 focus:ring-primary-500'} ${isViewMode ? 'bg-secondary-50 text-secondary-700' : ''}`}
                  disabled={isViewMode}
                >
                  <option value="">Selecione uma categoria</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.nome}</option>
                  ))}
                </select>
                {errors.categoria_id && <span className="text-red-500 text-xs">{errors.categoria_id}</span>}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-secondary-700">
                    Marca
                  </label>
                  {!isViewMode && (
                    <button
                      type="button"
                      onClick={() => setShowQuickAdd({ ...showQuickAdd, brand: !showQuickAdd.brand })}
                      className="text-xs text-primary-600 hover:text-primary-800"
                    >
                      + Nova
                    </button>
                  )}
                </div>
                {!isViewMode && showQuickAdd.brand ? (
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={quickAddData.brand}
                      onChange={(e) => setQuickAddData({ ...quickAddData, brand: e.target.value })}
                      placeholder="Nome da marca"
                      className="flex-1 px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => createBrandMutation.mutate({ nome: quickAddData.brand })}
                      disabled={!quickAddData.brand || createBrandMutation.isPending || createBrandMutation.isLoading}
                      className="px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 text-sm"
                    >
                      {createBrandMutation.isPending || createBrandMutation.isLoading ? '...' : 'Salvar'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowQuickAdd({ ...showQuickAdd, brand: false })}
                      className="px-3 py-2 bg-secondary-200 text-secondary-700 rounded-md hover:bg-secondary-300 text-sm"
                    >
                      Cancelar
                    </button>
                  </div>
                ) : null}
                <select
                  value={formData.marca_id}
                  onChange={(e) => !isViewMode && setFormData({ ...formData, marca_id: e.target.value })}
                  className={`w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none ${isViewMode ? 'bg-secondary-50 text-secondary-700' : 'focus:ring-2 focus:ring-primary-500'}`}
                  disabled={isViewMode}
                >
                  <option value="">Selecione uma marca</option>
                  {brands.map(brand => (
                    <option key={brand.id} value={brand.id}>{brand.nome}</option>
                  ))}
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-secondary-700">
                    Unidade *
                  </label>
                  {!isViewMode && (
                    <button
                      type="button"
                      onClick={() => setShowQuickAdd({ ...showQuickAdd, unit: !showQuickAdd.unit })}
                      className="text-xs text-primary-600 hover:text-primary-800"
                    >
                      + Nova
                    </button>
                  )}
                </div>
                {!isViewMode && showQuickAdd.unit ? (
                  <div className="space-y-2 mb-2 p-3 bg-secondary-50 rounded-md">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={quickAddData.unit}
                        onChange={(e) => setQuickAddData({ ...quickAddData, unit: e.target.value })}
                        placeholder="Sigla (ex: Unid, Barra 3m)"
                        className="flex-1 px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                      />
                      <input
                        type="text"
                        value={quickAddData.unitDesc}
                        onChange={(e) => setQuickAddData({ ...quickAddData, unitDesc: e.target.value })}
                        placeholder="Descrição (opcional)"
                        className="flex-1 px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => createUnitMutation.mutate({ 
                          sigla: quickAddData.unit, 
                          descricao: quickAddData.unitDesc || null 
                        })}
                        disabled={!quickAddData.unit || createUnitMutation.isPending || createUnitMutation.isLoading}
                        className="px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 text-sm"
                      >
                        {createUnitMutation.isPending || createUnitMutation.isLoading ? '...' : 'Salvar'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowQuickAdd({ ...showQuickAdd, unit: false })}
                        className="px-3 py-2 bg-secondary-200 text-secondary-700 rounded-md hover:bg-secondary-300 text-sm"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : null}
                <select
                  value={formData.unidade_id}
                  onChange={(e) => {
                    if (errors.unidade_id) setErrors({ ...errors, unidade_id: undefined });
                    !isViewMode && setFormData({ ...formData, unidade_id: e.target.value });
                  }}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none ${errors.unidade_id ? 'border-red-500 focus:ring-2 focus:ring-red-500' : 'border-secondary-300 focus:ring-2 focus:ring-primary-500'} ${isViewMode ? 'bg-secondary-50 text-secondary-700' : ''}`}
                  disabled={isViewMode}
                >
                  <option value="">Selecione uma unidade</option>
                  {units.map(unit => (
                    <option key={unit.id} value={unit.id}>{unit.sigla}</option>
                  ))}
                </select>
                {errors.unidade_id && <span className="text-red-500 text-xs">{errors.unidade_id}</span>}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Setor <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.setor_id}
                  onChange={(e) => !isViewMode && setFormData({ ...formData, setor_id: e.target.value })}
                  className={`w-full px-3 py-2 border ${errors.setor_id ? 'border-red-400 bg-red-50/20' : 'border-secondary-300'} rounded-md focus:outline-none ${isViewMode ? 'bg-secondary-50 text-secondary-700' : 'focus:ring-2 focus:ring-primary-500'}`}
                  disabled={isViewMode}
                >
                  <option value="">Selecione o setor</option>
                  {setores.map(setor => (
                    <option key={setor.id} value={setor.id}>{setor.nome}</option>
                  ))}
                </select>
                {errors.setor_id && <span className="text-red-500 text-xs mt-1 block">{errors.setor_id}</span>}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => !isViewMode && setFormData({ ...formData, descricao: e.target.value })}
                  rows="3"
                  className={`w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none ${isViewMode ? 'bg-secondary-50 text-secondary-700' : 'focus:ring-2 focus:ring-primary-500'}`}
                  readOnly={isViewMode}
                />
              </div>
            </div>

            {/* Imagem do Produto */}
            <div className="mt-4 pt-4 border-t border-secondary-200">
              <h4 className="text-sm font-medium text-secondary-700 mb-3">Imagem do Produto</h4>
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  {imagemPreview ? (
                    <div className="relative">
                      <img src={imagemPreview} alt="Preview" className="w-24 h-24 object-cover rounded-lg border border-secondary-300" />
                      {!isViewMode && (
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="w-24 h-24 flex items-center justify-center bg-secondary-100 rounded-lg border border-secondary-300">
                      <ImageIcon className="w-8 h-8 text-secondary-400" />
                    </div>
                  )}
                </div>
                <div>
                  {!isViewMode && (
                    <>
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="imagem-upload"
                      />
                      <label
                        htmlFor="imagem-upload"
                        className={`flex items-center gap-2 px-4 py-2 bg-secondary-100 text-secondary-700 rounded-lg border border-secondary-300 cursor-pointer hover:bg-secondary-200 transition-colors ${uploadingImage ? 'opacity-50 pointer-events-none' : ''}`}
                      >
                        <Upload className="w-4 h-4" />
                        {uploadingImage ? 'Enviando...' : imagemPreview ? 'Trocar imagem' : 'Enviar imagem'}
                      </label>
                      <p className="text-xs text-secondary-500 mt-1">Máximo 5MB (JPEG, PNG, GIF, WebP)</p>
                    </>
                  )}
                  {isViewMode && !imagemPreview && (
                    <p className="text-sm text-secondary-500">Nenhuma imagem cadastrada</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Variantes */}
          <div className="mb-4 sm:mb-6 lg:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-3 sm:mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-secondary-900">Variantes do Produto</h3>
              {!isViewMode && (
                <button
                  type="button"
                  onClick={addVariant}
                  className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm sm:text-base w-full sm:w-auto"
                >
                  <Plus className="w-4 h-4" />
                  <span>Adicionar Variante</span>
                </button>
              )}
            </div>

            {variants.length === 0 ? (
              <div className="text-center py-8 text-secondary-500">
                Nenhuma variante adicionada. Clique em "Adicionar Variante" para começar.
              </div>
            ) : (
              <div className="space-y-6">
                {variants.map((variant, variantIndex) => {
                  const compVal = parseFloat(variant.comprimento_cm);
                  const largVal = parseFloat(variant.largura_cm);
                  const hasArea = compVal > 0 && largVal > 0;
                  const areaM2 = hasArea ? (compVal * largVal) / 10000 : null;
                  const custoM2 = areaM2 && parseFloat(variant.preco_custo) > 0 ? parseFloat(variant.preco_custo) / areaM2 : null;
                  const vendaM2 = areaM2 && parseFloat(variant.preco_venda) > 0 ? parseFloat(variant.preco_venda) / areaM2 : null;

                  const hasSuppliers = Array.isArray(variant.fornecedores_precos) && variant.fornecedores_precos.length > 0;
                  const prefSupplier = hasSuppliers
                    ? (variant.fornecedores_precos.find(f => f.is_preferencial) || variant.fornecedores_precos[0])
                    : null;
                  const prefSupplierObj = prefSupplier
                    ? suppliers.find(s => String(s.id) === String(prefSupplier.fornecedor_id))
                    : null;
                  const prefSupplierName = prefSupplierObj
                    ? (prefSupplierObj.name || prefSupplierObj.nome)
                    : null;

                  return (
                    <div key={variant.id} className="border border-secondary-200 rounded-lg p-3 sm:p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-3 sm:mb-4">
                        <div className="flex items-center gap-3">
                          <h4 className="text-sm sm:text-base font-semibold text-secondary-900">
                            Variante {variantIndex + 1}
                          </h4>
                          {parseFloat(variant.preco_venda) > 0 && parseFloat(variant.preco_custo) > 0 && (
                            <div className="flex items-center gap-1.5 text-xs bg-emerald-50 text-emerald-800 px-2.5 py-0.5 rounded-full border border-emerald-200 font-medium">
                              <span>Margem: <strong>{(((parseFloat(variant.preco_venda) - parseFloat(variant.preco_custo)) / parseFloat(variant.preco_venda)) * 100).toFixed(1)}%</strong></span>
                              <span>•</span>
                              <span>Lucro: <strong>R$ {(parseFloat(variant.preco_venda) - parseFloat(variant.preco_custo)).toFixed(2)}</strong></span>
                              {vendaM2 && (
                                <>
                                  <span>•</span>
                                  <span>Venda: <strong>R$ {vendaM2.toFixed(2)}/m²</strong></span>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      <div className="flex items-center gap-2">
                        {!isViewMode && (
                          <button
                            type="button"
                            onClick={() => duplicateVariant(variantIndex)}
                            className="px-2 py-1 text-xs bg-secondary-100 hover:bg-secondary-200 text-secondary-700 rounded flex items-center gap-1 font-medium transition-colors border border-secondary-300"
                            title="Duplicar medidas, preços e fornecedores desta variante"
                          >
                            <span>📋 Duplicar</span>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedVariantForLabel(variant);
                            setShowLabelModal(true);
                          }}
                          className="text-primary-600 hover:text-primary-800 p-1"
                          title="Gerar Etiqueta"
                        >
                          <QrCode className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        {!isViewMode && (
                          <button
                            type="button"
                            onClick={() => removeVariant(variantIndex)}
                            className="text-red-600 hover:text-red-800 p-1"
                          >
                            <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-3 sm:mb-4">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          Cor
                        </label>
                        <input
                          type="text"
                          value={variant.cor}
                          onChange={(e) => !isViewMode && updateVariant(variantIndex, 'cor', e.target.value)}
                          className={`w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none ${isViewMode ? 'bg-secondary-50 text-secondary-700' : 'focus:ring-2 focus:ring-primary-500'}`}
                          placeholder="Ex: Branco, Preto"
                          disabled={isViewMode}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          Tamanho
                        </label>
                        <input
                          type="text"
                          value={variant.tamanho_label}
                          onChange={(e) => !isViewMode && updateVariant(variantIndex, 'tamanho_label', e.target.value)}
                          className={`w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none ${isViewMode ? 'bg-secondary-50 text-secondary-700' : 'focus:ring-2 focus:ring-primary-500'}`}
                          placeholder="Ex: 25CM, 128MM"
                          disabled={isViewMode}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          Medida
                        </label>
                        <input
                          type="text"
                          value={variant.medida_text}
                          onChange={(e) => !isViewMode && updateVariant(variantIndex, 'medida_text', e.target.value)}
                          className={`w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none ${isViewMode ? 'bg-secondary-50 text-secondary-700' : 'focus:ring-2 focus:ring-primary-500'}`}
                          placeholder="Ex: 25cm, 128mm"
                          disabled={isViewMode}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          Espessura (mm)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={variant.espessura_mm}
                          onChange={(e) => !isViewMode && updateVariant(variantIndex, 'espessura_mm', e.target.value)}
                          className={`w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none ${isViewMode ? 'bg-secondary-50 text-secondary-700' : 'focus:ring-2 focus:ring-primary-500'}`}
                          placeholder="Ex: 15, 18"
                          disabled={isViewMode}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          Comprimento (cm)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={variant.comprimento_cm}
                          onChange={(e) => !isViewMode && updateVariant(variantIndex, 'comprimento_cm', e.target.value)}
                          className={`w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none ${isViewMode ? 'bg-secondary-50 text-secondary-700' : 'focus:ring-2 focus:ring-primary-500'}`}
                          placeholder="Ex: 100"
                          disabled={isViewMode}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          Largura (cm)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={variant.largura_cm}
                          onChange={(e) => !isViewMode && updateVariant(variantIndex, 'largura_cm', e.target.value)}
                          className={`w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none ${isViewMode ? 'bg-secondary-50 text-secondary-700' : 'focus:ring-2 focus:ring-primary-500'}`}
                          placeholder="Ex: 50"
                          disabled={isViewMode}
                        />
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-sm font-medium text-secondary-700">
                            Preço de Custo
                          </label>
                          {hasSuppliers ? (
                            <span
                              className="text-[11px] text-emerald-800 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200 font-medium flex items-center gap-1"
                              title={prefSupplierName ? `Sincronizado com fornecedor preferencial: ${prefSupplierName}` : 'Sincronizado com fornecedor preferencial'}
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                              <span>{prefSupplierName ? `${prefSupplierName}` : 'Preferencial'}</span>
                            </span>
                          ) : (
                            <span className="text-[11px] text-gray-500">(Manual)</span>
                          )}
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          value={variant.preco_custo}
                          onChange={(e) => !isViewMode && updateVariant(variantIndex, 'preco_custo', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none ${
                            hasSuppliers
                              ? 'bg-emerald-50/40 border-emerald-300 text-emerald-950 font-semibold focus:ring-2 focus:ring-emerald-500'
                              : isViewMode
                              ? 'bg-secondary-50 text-secondary-700 border-secondary-300'
                              : 'border-secondary-300 focus:ring-2 focus:ring-primary-500'
                          }`}
                          placeholder="0.00"
                          disabled={isViewMode}
                        />
                        {hasSuppliers && (
                          <p className="text-[10px] text-emerald-700 mt-0.5">
                            ⚡ Sincronizado com o fornecedor preferencial abaixo
                          </p>
                        )}
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-sm font-medium text-secondary-700">
                            Preço de Venda
                          </label>
                          {hasSuppliers ? (
                            <span
                              className="text-[11px] text-emerald-800 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200 font-medium flex items-center gap-1"
                              title={prefSupplierName ? `Sincronizado com fornecedor preferencial: ${prefSupplierName}` : 'Sincronizado com fornecedor preferencial'}
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                              <span>{prefSupplierName ? `${prefSupplierName}` : 'Preferencial'}</span>
                            </span>
                          ) : (
                            <span className="text-[11px] text-gray-500">(Manual)</span>
                          )}
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          value={variant.preco_venda}
                          onChange={(e) => !isViewMode && updateVariant(variantIndex, 'preco_venda', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none ${
                            hasSuppliers
                              ? 'bg-emerald-50/40 border-emerald-300 text-emerald-950 font-semibold focus:ring-2 focus:ring-emerald-500'
                              : isViewMode
                              ? 'bg-secondary-50 text-secondary-700 border-secondary-300'
                              : 'border-secondary-300 focus:ring-2 focus:ring-primary-500'
                          }`}
                          placeholder="0.00"
                          disabled={isViewMode}
                        />
                        {hasSuppliers && (
                          <p className="text-[10px] text-emerald-700 mt-0.5">
                            ⚡ Sincronizado com o fornecedor preferencial abaixo
                          </p>
                        )}
                      </div>

                      {hasArea && (
                        <div className="sm:col-span-2 lg:col-span-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 flex flex-wrap items-center justify-between gap-3 shadow-sm">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-blue-950">📐 Área Calculada:</span>
                            <span className="font-mono font-bold bg-white px-2 py-0.5 rounded border border-blue-200 text-blue-800">
                              {areaM2.toFixed(4)} m²
                            </span>
                            <span className="text-blue-600">({compVal}cm × {largVal}cm)</span>
                          </div>
                          <div className="flex items-center gap-3">
                            {custoM2 ? (
                              <span className="bg-white/80 px-2.5 py-1 rounded border border-blue-200 font-medium">
                                Custo m²: <strong className="text-blue-950 font-mono">R$ {custoM2.toFixed(2)}/m²</strong>
                              </span>
                            ) : null}
                            {vendaM2 ? (
                              <span className="bg-emerald-50 px-2.5 py-1 rounded border border-emerald-300 font-medium text-emerald-800">
                                Venda m²: <strong className="text-emerald-900 font-mono">R$ {vendaM2.toFixed(2)}/m²</strong>
                              </span>
                            ) : null}
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Acabamento</label>
                        <select
                          value={variant.acabamento || ''}
                          onChange={(e) => !isViewMode && updateVariant(variantIndex, 'acabamento', e.target.value)}
                          className={`w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none ${isViewMode ? 'bg-secondary-50' : 'focus:ring-2 focus:ring-primary-500'}`}
                          disabled={isViewMode}
                        >
                          <option value="">Selecione</option>
                          <option value="Fosco">Fosco</option>
                          <option value="Brilho">Brilho</option>
                          <option value="Acetinado">Acetinado</option>
                          <option value="Fosco/Semi-Brilho">Fosco/Semi-Brilho</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Diâmetro (mm)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={variant.diametro_mm || ''}
                          onChange={(e) => !isViewMode && updateVariant(variantIndex, 'diametro_mm', e.target.value)}
                          className={`w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none ${isViewMode ? 'bg-secondary-50' : 'focus:ring-2 focus:ring-primary-500'}`}
                          placeholder="0.00"
                          disabled={isViewMode}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          SKU Variação
                          <span className="ml-1.5 text-xs text-green-600 font-normal">(auto)</span>
                        </label>
                        <input
                          type="text"
                          value={variant.sku_variacao || ''}
                          onChange={(e) => !isViewMode && updateVariant(variantIndex, 'sku_variacao', e.target.value)}
                          className={`w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none ${isViewMode ? 'bg-secondary-50 text-secondary-700' : 'focus:ring-2 focus:ring-primary-500'}`}
                          placeholder={
                            formData.sku
                              ? (variant.espessura_mm ? `${formData.sku}-${Math.round(parseFloat(variant.espessura_mm))}MM` : `${formData.sku}-V${variantIndex + 1}`)
                              : `Auto: V${variantIndex + 1}`
                          }
                          disabled={isViewMode}
                        />
                        <p className="text-[10px] text-gray-400 mt-0.5">Gerado automaticamente pelo sistema se deixado em branco</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Código Barras</label>
                        <input
                          type="text"
                          value={variant.barcode || ''}
                          onChange={(e) => !isViewMode && updateVariant(variantIndex, 'barcode', e.target.value)}
                          className={`w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none ${isViewMode ? 'bg-secondary-50' : 'focus:ring-2 focus:ring-primary-500'}`}
                          placeholder="7891234567890"
                          disabled={isViewMode}
                        />
                      </div>
                    </div>

                    {/* Row 4 - referencia_fornecedor and taxes */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Referência Fornecedor</label>
                        <input
                          type="text"
                          value={variant.referencia_fornecedor || ''}
                          onChange={(e) => !isViewMode && updateVariant(variantIndex, 'referencia_fornecedor', e.target.value)}
                          className={`w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none ${isViewMode ? 'bg-secondary-50' : 'focus:ring-2 focus:ring-primary-500'}`}
                          placeholder="REF-FORN-001"
                          disabled={isViewMode}
                        />
                      </div>

                      {taxesData && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 mb-1">Imposto</label>
                            <select
                              value={variant.imposto_id || ''}
                              onChange={(e) => !isViewMode && updateVariant(variantIndex, 'imposto_id', e.target.value ? parseInt(e.target.value) : null)}
                              className={`w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none ${isViewMode ? 'bg-secondary-50' : 'focus:ring-2 focus:ring-primary-500'}`}
                              disabled={isViewMode}
                            >
                              <option value="">Sem imposto</option>
                              {(taxesData.data || []).map(tax => (
                                <option key={tax.id} value={tax.id}>{tax.nome} ({tax.aliquota}%)</option>
                              ))}
                            </select>
                          </div>

                          <div className="flex items-center gap-2 pt-6">
                            <input
                              type="checkbox"
                              id={`imposto_incluido_${variantIndex}`}
                              checked={variant.imposto_incluido || false}
                              onChange={(e) => !isViewMode && updateVariant(variantIndex, 'imposto_incluido', e.target.checked)}
                              className="rounded"
                              disabled={isViewMode}
                            />
                            <label htmlFor={`imposto_incluido_${variantIndex}`} className="text-sm text-secondary-700">
                              Preço com imposto incluído
                            </label>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Preços por Fornecedor */}
                    <div className="mt-4 pt-4 border-t border-secondary-200">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          <span className="p-1 bg-purple-50 text-purple-700 rounded-md border border-purple-200">
                            <Building2 className="w-4 h-4" />
                          </span>
                          <h5 className="text-sm font-bold text-secondary-900">
                            Preços por Fornecedor
                          </h5>
                          {variant.fornecedores_precos && variant.fornecedores_precos.length > 0 && (
                            <span className="text-[11px] font-semibold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-200">
                              {variant.fornecedores_precos.length} vinculado{variant.fornecedores_precos.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>

                        {!isViewMode && (
                          <button
                            type="button"
                            onClick={() => addSupplierPrice(variantIndex)}
                            className="text-xs font-semibold px-2.5 py-1.5 bg-primary-50 text-primary-700 hover:bg-primary-100 border border-primary-200 rounded-lg flex items-center gap-1.5 shadow-2xs transition-colors self-start sm:self-auto"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            <span>Adicionar Fornecedor</span>
                          </button>
                        )}
                      </div>

                      {variant.fornecedores_precos && variant.fornecedores_precos.length > 0 ? (
                        <div className="space-y-2.5">
                          {variant.fornecedores_precos.map((supplierPrice, priceIndex) => {
                            const otherSelectedIds = new Set(
                              (variant.fornecedores_precos || [])
                                .filter((_, idx) => idx !== priceIndex)
                                .map(p => String(p.fornecedor_id))
                            );

                            return (
                              <div
                                key={supplierPrice.id}
                                className={`p-2.5 sm:p-3 rounded-lg border transition-all ${
                                  supplierPrice.is_preferencial
                                    ? 'bg-gradient-to-r from-emerald-50/70 to-emerald-50/30 border-emerald-300 shadow-2xs'
                                    : 'bg-secondary-50 border-secondary-200'
                                }`}
                              >
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-2 sm:gap-2.5 items-center">
                                  {/* Select Fornecedor */}
                                  <div className="lg:col-span-4">
                                    <label className="block text-[11px] font-semibold text-secondary-600 mb-0.5">
                                      Fornecedor
                                    </label>
                                    <select
                                      value={supplierPrice.fornecedor_id || ''}
                                      onChange={(e) => !isViewMode && updateSupplierPrice(variantIndex, priceIndex, 'fornecedor_id', e.target.value)}
                                      className={`w-full px-2.5 py-1.5 border border-secondary-300 rounded-md focus:outline-none text-xs font-medium ${
                                        isViewMode ? 'bg-secondary-100 text-secondary-700' : 'bg-white focus:ring-2 focus:ring-primary-500'
                                      }`}
                                      disabled={isViewMode}
                                    >
                                      <option value="">Selecione o fornecedor</option>
                                      {suppliers.map(supplier => {
                                        const isAlreadySelected = otherSelectedIds.has(String(supplier.id));
                                        return (
                                          <option key={supplier.id} value={supplier.id} disabled={isAlreadySelected}>
                                            {supplier.name || supplier.nome} {isAlreadySelected ? '(já adicionado)' : ''}
                                          </option>
                                        );
                                      })}
                                    </select>
                                  </div>

                                  {/* Referência do Fornecedor */}
                                  <div className="lg:col-span-2">
                                    <label className="block text-[11px] font-semibold text-secondary-600 mb-0.5">
                                      Ref. Fornecedor
                                    </label>
                                    <input
                                      type="text"
                                      value={supplierPrice.referencia_fornecedor || ''}
                                      onChange={(e) => !isViewMode && updateSupplierPrice(variantIndex, priceIndex, 'referencia_fornecedor', e.target.value)}
                                      placeholder="Ex: PAL-01"
                                      className={`w-full px-2.5 py-1.5 border border-secondary-300 rounded-md focus:outline-none text-xs ${
                                        isViewMode ? 'bg-secondary-100 text-secondary-700' : 'bg-white focus:ring-2 focus:ring-primary-500'
                                      }`}
                                      disabled={isViewMode}
                                    />
                                  </div>

                                  {/* Preço de Custo */}
                                  <div className="lg:col-span-2">
                                    <label className="block text-[11px] font-semibold text-secondary-600 mb-0.5">
                                      Preço Custo (R$)
                                    </label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={supplierPrice.preco_custo !== undefined ? supplierPrice.preco_custo : (supplierPrice.preco || '')}
                                      onChange={(e) => !isViewMode && updateSupplierPrice(variantIndex, priceIndex, 'preco_custo', e.target.value)}
                                      placeholder="0.00"
                                      className={`w-full px-2.5 py-1.5 border border-secondary-300 rounded-md focus:outline-none text-xs font-semibold text-gray-900 ${
                                        isViewMode ? 'bg-secondary-100 text-secondary-700' : 'bg-white focus:ring-2 focus:ring-primary-500'
                                      }`}
                                      disabled={isViewMode}
                                    />
                                  </div>

                                  {/* Preço de Venda */}
                                  <div className="lg:col-span-2">
                                    <label className="block text-[11px] font-semibold text-secondary-600 mb-0.5">
                                      Preço Venda (R$)
                                    </label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={supplierPrice.preco_venda || ''}
                                      onChange={(e) => !isViewMode && updateSupplierPrice(variantIndex, priceIndex, 'preco_venda', e.target.value)}
                                      placeholder="0.00"
                                      className={`w-full px-2.5 py-1.5 border border-secondary-300 rounded-md focus:outline-none text-xs font-semibold text-emerald-800 ${
                                        isViewMode ? 'bg-secondary-100 text-secondary-700' : 'bg-white focus:ring-2 focus:ring-primary-500'
                                      }`}
                                      disabled={isViewMode}
                                    />
                                  </div>

                                  {/* Preferencial e Ações */}
                                  <div className="lg:col-span-2 flex items-center justify-between sm:justify-end gap-1.5 pt-4 sm:pt-4">
                                    <button
                                      type="button"
                                      onClick={() => !isViewMode && updateSupplierPrice(variantIndex, priceIndex, 'is_preferencial', !supplierPrice.is_preferencial)}
                                      disabled={isViewMode}
                                      className={`px-2 py-1.5 rounded-md text-[11px] font-semibold transition-all flex items-center gap-1 border shadow-2xs ${
                                        supplierPrice.is_preferencial
                                          ? 'bg-emerald-600 text-white border-emerald-600'
                                          : 'bg-white text-gray-700 hover:bg-gray-100 border-gray-300'
                                      }`}
                                      title={supplierPrice.is_preferencial ? 'Fornecedor preferencial ativo desta variante' : 'Definir como fornecedor preferencial desta variante'}
                                    >
                                      <Star className={`w-3.5 h-3.5 ${supplierPrice.is_preferencial ? 'fill-current text-amber-300' : 'text-gray-400'}`} />
                                      <span>{supplierPrice.is_preferencial ? 'Preferencial' : 'Preferencial'}</span>
                                    </button>

                                    {!isViewMode && variants.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() => applySupplierToAllVariants(variantIndex, priceIndex)}
                                        className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 bg-white border border-blue-200 rounded-md transition-colors shadow-2xs"
                                        title="Replicar este fornecedor e preços para todas as variantes do produto"
                                      >
                                        <Copy className="w-3.5 h-3.5" />
                                      </button>
                                    )}

                                    {!isViewMode && (
                                      <button
                                        type="button"
                                        onClick={() => removeSupplierPrice(variantIndex, priceIndex)}
                                        className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 bg-white border border-red-200 rounded-md transition-colors shadow-2xs"
                                        title="Remover fornecedor desta variante"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="bg-secondary-50/70 border border-secondary-200/80 rounded-lg p-3 text-xs text-secondary-600 flex items-center gap-2">
                          <Info className="w-4 h-4 text-secondary-400 flex-shrink-0" />
                          <span>
                            Nenhum fornecedor vinculado. O produto utiliza os preços definidos manualmente no topo da variante. Clique em <strong>"Adicionar Fornecedor"</strong> para incluir tabelas de compra por distribuidor.
                          </span>
                        </div>
                      )}
                    </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </form>
        )}

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 p-3 sm:p-4 lg:p-6 border-t bg-secondary-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-secondary-700 bg-white border border-secondary-300 rounded-md hover:bg-secondary-50 transition-colors text-sm sm:text-base order-2 sm:order-1"
          >
            {isViewMode ? 'Fechar' : 'Cancelar'}
          </button>
          {!isViewMode && (
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isEditing ? (updateProductMutation.isPending || updateProductMutation.isLoading) : (createProductMutation.isPending || createProductMutation.isLoading)}
              className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base order-1 sm:order-2"
            >
              <Save className="w-4 h-4" />
              <span className="hidden sm:inline">
                {isEditing 
                  ? ((updateProductMutation.isPending || updateProductMutation.isLoading) ? 'Salvando...' : 'Salvar Alterações')
                  : ((createProductMutation.isPending || createProductMutation.isLoading) ? 'Salvando...' : 'Salvar Produto')
                }
              </span>
              <span className="sm:hidden">
                {isEditing 
                  ? ((updateProductMutation.isPending || updateProductMutation.isLoading) ? 'Salvando...' : 'Salvar')
                  : ((createProductMutation.isPending || createProductMutation.isLoading) ? 'Salvando...' : 'Salvar')
                }
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Modal de Etiqueta com QR Code */}
      {showLabelModal && (
        <ProductLabelModal
          product={{
            id: product?.id,
            nome: formData.nome,
            sku: formData.sku
          }}
          variant={selectedVariantForLabel}
          onClose={() => {
            setShowLabelModal(false);
            setSelectedVariantForLabel(null);
          }}
        />
      )}
    </div>
  );
};

export default ProductFormComplete;

